import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Overlay } from './Overlay';
import { SnakSlide } from '../experience/SnakSlide';
import { storyboard } from '../../data/storyboard';
import type { Format } from '../../types/snak';
interface StoryboardOverlayProps {
  format: Format;
  onFormatChange: (format: Format) => void;
  onClose: () => void;
}
export function StoryboardOverlay({
  format,
  onFormatChange,
  onClose
}: StoryboardOverlayProps) {
  const compact = format === 'mobile';
  const [index, setIndex] = useState(0);
  const slide = storyboard[index];
  const last = storyboard.length - 1;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') setIndex(i => Math.min(i + 1, last));
      if (event.key === 'ArrowLeft') setIndex(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [last]);
  const width = compact ? 236 : 296;
  return <Overlay eyebrow="Storyboard" format={format} onFormatChange={onFormatChange} onClose={onClose} frame={false} data-id="element-248" data-mp="sblr9f903pex">
      <div className={['mx-auto flex max-w-[1180px] items-center gap-10 px-6', compact ? 'flex-col py-8' : 'flex-col py-10 lg:flex-row lg:gap-16'].join(' ')} data-id="element-249" data-mp="dlwf6ozde0zv">
        <div className="flex shrink-0 items-center gap-3 sm:gap-5" data-id="element-250" data-mp="4nkqwtlz7fal">
          <Arrow label="Previous slide" disabled={index === 0} onClick={() => setIndex(i => Math.max(i - 1, 0))} data-id="element-251" data-mp="9lcejbfl8f8z">
            <CaretLeft size={16} weight="bold" aria-hidden="true" data-id="element-252" data-mp="3vr9cm3xusw95" />
          </Arrow>

          <div className="rounded-[36px] bg-[#0B1512] p-[9px] shadow-[0_40px_80px_-40px_rgba(1,51,43,0.5)]" data-id="element-253" data-mp="1p1bieai69p3f">
            <AnimatePresence mode="wait" data-id="element-254" data-mp="1u063xk7ixrr8">
              <motion.div key={slide.step} initial={{
              opacity: 0,
              x: 18
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -18
            }} transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1]
            }} data-id="element-255" data-mp="1tzwa1ljcz0g9">
                <SnakSlide step={slide.step} width={width} label={`Storyboard slide ${index + 1}: ${slide.title}`} data-id="element-256" data-mp="1ilum3zpsmfsi" />
              </motion.div>
            </AnimatePresence>
          </div>

          <Arrow label="Next slide" disabled={index === last} onClick={() => setIndex(i => Math.min(i + 1, last))} data-id="element-257" data-mp="26axcbvyhtgym">
            <CaretRight size={16} weight="bold" aria-hidden="true" data-id="element-258" data-mp="21hufneyf5re7" />
          </Arrow>
        </div>

        <div className={compact ? 'w-full' : 'w-full max-w-[420px]'} data-id="element-259" data-mp="1xg97u80qs3av">
          <span className="font-body text-[10px] uppercase tracking-[0.22em] text-[rgba(1,51,43,0.4)]" data-id="element-260" data-mp="2u11gc4flfiq5">
            Slide {String(index + 1).padStart(2, '0')} /{' '}
            {String(storyboard.length).padStart(2, '0')}
          </span>
          <AnimatePresence mode="wait" data-id="element-261" data-mp="bt88n3dt76jx">
            <motion.div key={slide.step} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.3
          }} data-id="element-262" data-mp="gp6rkbgdbwcy">
              <h2 className={['mt-3 font-heading leading-[1.08] tracking-[-0.02em] text-background', compact ? 'text-[24px]' : 'text-[30px]'].join(' ')} data-id="element-263" data-mp="pok1f58emmy7">
                {slide.title}
              </h2>
              <p className="mt-4 max-w-[46ch] font-body text-[14px] leading-[1.7] text-[rgba(1,51,43,0.65)]" data-id="element-264" data-mp="14gcefakq7owr">
                {slide.note}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex flex-wrap gap-1.5 border-t border-[rgba(1,51,43,0.12)] pt-5" data-id="element-265" data-mp="1de4n3vvxzyb6">
            {storyboard.map((item, i) => <button key={item.step} type="button" onClick={() => setIndex(i)} aria-label={`Go to slide ${i + 1}: ${item.title}`} aria-current={i === index ? 'true' : undefined} className={['h-8 w-8 rounded-full font-body text-[11px] tabular-nums transition-colors', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary', i === index ? 'bg-background text-white' : 'bg-white text-[rgba(1,51,43,0.5)] ring-1 ring-[rgba(1,51,43,0.12)] hover:text-background'].join(' ')} data-id="element-266" data-mp="14rtl3renn0in" data-mpi={i}>
                {String(i + 1).padStart(2, '0')}
              </button>)}
          </div>
        </div>
      </div>
    </Overlay>;
}
function Arrow({
  children,
  label,
  disabled,
  onClick
}: {
  children: React.ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} disabled={disabled} aria-label={label} className={['inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-white/70 transition-colors', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary', disabled ? 'pointer-events-none border-[rgba(1,51,43,0.1)] text-[rgba(1,51,43,0.2)]' : 'border-[rgba(1,51,43,0.18)] text-background hover:border-[rgba(1,51,43,0.5)]'].join(' ')} data-id="element-267" data-mp="29qqix37mh2w7">
      {children}
    </button>;
}